import type { LucideIcon } from 'lucide-react';
import {
  AlertTriangle,
  ChartColumnBig,
  Bot,
  Building2,
  Building,
  History,
  ClipboardCheck,
  LayoutDashboard,
  ListChecks,
  MessagesSquare,
  PlayCircle,
  Wrench,
} from 'lucide-react';
import { Roles, type AccessRequirement } from '@/auth/roles';

export interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  access?: AccessRequirement;
  /** Live count shown next to the label (key into `useNavCounts()`). */
  badge?: 'reviews' | 'escalations';
}

export interface NavSection {
  labelKey: string;
  items: NavItem[];
}

const reviewers: AccessRequirement = {
  roles: [Roles.PlatformAdmin, Roles.PlatformOperator, Roles.TenantAdmin, Roles.TenantReviewer],
};
const platformAdmin: AccessRequirement = { roles: [Roles.PlatformAdmin] };
const tenantAdmins: AccessRequirement = { roles: [Roles.PlatformAdmin, Roles.TenantAdmin] };
const tenantAdminOnly: AccessRequirement = { roles: [Roles.TenantAdmin] };

/** Navigation per docs/spec/08 §1. Access requirements are UX only; the API is the authority. */
export const navSections: NavSection[] = [
  {
    labelKey: 'nav.build',
    items: [
      { to: '/', labelKey: 'nav.dashboard', icon: LayoutDashboard },
      { to: '/agents', labelKey: 'nav.agents', icon: Bot },
      { to: '/rulesets', labelKey: 'nav.rulesets', icon: ListChecks },
    ],
  },
  {
    labelKey: 'nav.operate',
    items: [
      { to: '/runs', labelKey: 'nav.runs', icon: PlayCircle },
      { to: '/metrics', labelKey: 'nav.metrics', icon: ChartColumnBig },
      { to: '/conversations', labelKey: 'nav.conversations', icon: MessagesSquare, access: reviewers },
      { to: '/review', labelKey: 'nav.review', icon: ClipboardCheck, access: reviewers, badge: 'reviews' },
      { to: '/escalations', labelKey: 'nav.escalations', icon: AlertTriangle, access: reviewers, badge: 'escalations' },
    ],
  },
  {
    labelKey: 'nav.platform',
    items: [
      { to: '/tools', labelKey: 'nav.tools', icon: Wrench, access: platformAdmin },
      { to: '/tenants', labelKey: 'nav.tenants', icon: Building2, access: platformAdmin },
      { to: '/organization', labelKey: 'admin.nav.organization', icon: Building, access: tenantAdminOnly },
      { to: '/audit', labelKey: 'admin.nav.audit', icon: History, access: tenantAdmins },
    ],
  },
];

export const accessRules = { reviewers, platformAdmin, tenantAdmins } as const;
